(() => {
  console.log("[HVW] variant sort loaded");

  // Canonical alpha size order (true sizes)
  const ALPHA_ORDER = [
    "XXXS","XXS","XS","S","M","L","XL",
    "2XL","3XL","4XL","5XL","6XL","7XL","8XL"
  ];

  const clean = (s) => (s || "").toString().replace(/\u00A0/g, " ").trim();
  const up = (s) => clean(s).toUpperCase();

  function normalizeText(raw) {
    let v = up(raw);

    // Remove common prefixes
    v = v.replace(/^SIZE\s+/i, "");

    // Normalize separators/spaces early
    v = v.replace(/\s+/g, " ");
    v = v.replace(/\s*\/\s*/g, "/");

    // If it's a numeric size like "10.0" or "3.0", normalize to "10" / "3"
    // Also allow half sizes like "10.5"
    if (/^\d{1,2}\.0$/.test(v)) v = v.replace(/\.0$/, "");
    if (/^\d{1,2},\d$/.test(v)) v = v.replace(",", ".");

    // Strip trailing numeric/range parts for alpha labels:
    // "XL = 46", "SM = 38-40", "2XL/3XL 50-55"
    // AND attached versions: "L-42/44", "3XL=50/52", "2XL-48"
    // (Important: this should NOT kill pure shoe sizes, so only run when there are letters present)
    const hasLetters = /[A-Z]/.test(v);
    if (hasLetters) {
      // Remove anything after "=" if it’s followed by numbers
      v = v.replace(/\s*=\s*[\d\.\-\/ ]+$/i, "");
      // Remove attached "-<numbers>" like "L-42/44" or "2XL-48"
      v = v.replace(/([A-Z0-9\/]+)\s*-\s*[\d\.\-\/ ]+$/i, "$1");
      // Remove space-separated trailing numbers/ranges like "XL 46" or "2XL/3XL 50-55"
      v = v.replace(/\s+[\d\.]+(\s*[\-\/]\s*[\d\.]+)?$/i, "");
    }

    // --- KEY FIX: your pill abbreviations ---
    if (v === "SM") v = "S";
    if (v === "MD") v = "M";
    if (v === "LG" || v === "LGE" || v === "LARGE") v = "L";
    if (v === "LR") v = "L"; // treat LR like L for ordering

    // Word variants
    if (v === "XSMALL" || v === "X SMALL") v = "XS";
    if (v === "SMALL") v = "S";
    if (v === "MEDIUM") v = "M";
    if (v === "XLARGE" || v === "X LARGE" || v === "XLG" || v === "XLGE") v = "XL";

    // Convert XXL/XXXL to numeric XL format
    if (v === "XXL") v = "2XL";
    if (v === "XXXL") v = "3XL";

    // Convert "2X" style just in case
    v = v.replace(/^(\d)X(L)?$/i, "$1XL"); // 2X -> 2XL

    // SR/MR/LR codes sometimes appear
    if (v === "SR") v = "S";
    if (v === "MR") v = "M";
    if (v === "LR") v = "L";

    // Final cleanup again
    v = v.replace(/\s+/g, " ").trim();

    return v;
  }

  function codeToLeg(c) {
    if (c === "S") return "SHORT";
    if (c === "R") return "REGULAR";
    if (c === "L") return "LONG";
    if (c === "T") return "TALL";
    return null;
  }

  const LEG_ORDER = ["SHORT", "REGULAR", "LONG", "TALL"];

  function normalizeLeg(v) {
    const x = up(v).replace(/\./g, "");
    if (x === "S" || x === "SHORT") return "SHORT";
    if (x === "R" || x === "REG" || x === "REGULAR") return "REGULAR";
    if (x === "L" || x === "LONG") return "LONG";
    if (x === "T" || x === "TALL") return "TALL";
    return null;
  }

  // Parse into comparable key
  function parseKey(rawLabel) {
    const v0 = normalizeText(rawLabel);

    // Paired alpha "6XL/7XL" -> use left side
    if (v0.includes("/")) {
      const [left] = v0.split("/");
      const canonLeft = normalizeText(left);
      const idx = ALPHA_ORDER.indexOf(canonLeft);
      if (idx !== -1) return { type: "ALPHA", alphaIdx: idx, raw: canonLeft };
    }

    // Alpha
    const alphaIdx = ALPHA_ORDER.indexOf(v0);
    if (alphaIdx !== -1) return { type: "ALPHA", alphaIdx, raw: v0 };

    // Waist/leg composites
    let m = v0.match(/\bW(\d{2,3})\b.*\bL(\d{2,3})\b/i);
    if (m) return { type: "WAISTLEG", waist: +m[1], leg: +m[2], raw: v0 };

    m = v0.match(/^(\d{2,3})\s*\/\s*(\d{2,3})$/);
    if (m) return { type: "WAISTLEG", waist: +m[1], leg: +m[2], raw: v0 };

    // 32R / R32 (2–3 digit waist)
    m = v0.match(/^(\d{2,3})(S|R|L|T)$/);
    if (m) return { type: "WAISTCODE", waist: +m[1], code: codeToLeg(m[2]), raw: v0 };

    m = v0.match(/^(S|R|L|T)(\d{2,3})$/);
    if (m) return { type: "WAISTCODE", waist: +m[2], code: codeToLeg(m[1]), raw: v0 };

    // Leg-only
    const leg = normalizeLeg(v0);
    if (leg) return { type: "LEG", leg, raw: v0 };

    // Pure numeric (shoe sizes etc) — support 1–2 digits + optional .5
    if (/^\d{1,2}(\.5)?$/.test(v0)) {
      return { type: "NUM", num: parseFloat(v0), raw: v0 };
    }
    if (/^\d{1,2}\.\d$/.test(v0)) {
      return { type: "NUM", num: parseFloat(v0), raw: v0 };
    }

    return { type: "OTHER", raw: v0 };
  }

  const TYPE_WEIGHT = { ALPHA: 10, WAISTLEG: 20, WAISTCODE: 25, NUM: 30, LEG: 40, OTHER: 90 };

  function compareKeys(a, b) {
    const wa = TYPE_WEIGHT[a.type] ?? 99;
    const wb = TYPE_WEIGHT[b.type] ?? 99;
    if (wa !== wb) return wa - wb;

    if (a.type === "ALPHA") return a.alphaIdx - b.alphaIdx;

    if (a.type === "WAISTLEG") {
      if (a.waist !== b.waist) return a.waist - b.waist;
      return a.leg - b.leg;
    }

    if (a.type === "WAISTCODE") {
      if (a.waist !== b.waist) return a.waist - b.waist;
      return LEG_ORDER.indexOf(a.code) - LEG_ORDER.indexOf(b.code);
    }

    if (a.type === "NUM") return a.num - b.num;

    if (a.type === "LEG") return LEG_ORDER.indexOf(a.leg) - LEG_ORDER.indexOf(b.leg);

    return (a.raw || "").localeCompare(b.raw || "");
  }

  function getLabelText(el) {
    return clean(el.querySelector(".block-swatch__item-text")?.textContent || el.textContent || "");
  }

  function applyOnce() {
    const sizeOpt = [...document.querySelectorAll(".product-form__option")].find(o =>
      (o.querySelector(".product-form__option-name")?.textContent || "").toLowerCase().includes("size")
    );
    if (!sizeOpt) return false;

    const list = sizeOpt.querySelector(".block-swatch-list");
    if (!list) return false;

    const items = [...list.querySelectorAll(":scope > .block-swatch")];
    if (items.length < 2) return false;

    const labels = items.map(getLabelText);
    console.log("[HVW] labels:", labels);

    items
      .sort((x, y) => compareKeys(parseKey(getLabelText(x)), parseKey(getLabelText(y))))
      .forEach(el => list.appendChild(el));

    console.log("[HVW] applied sort");
    return true;
  }

  function waitAndApply() {
    if (!applyOnce()) setTimeout(waitAndApply, 100);
  }

  waitAndApply();
  document.addEventListener("variant:change", () => setTimeout(applyOnce, 80));
  document.addEventListener("change", () => setTimeout(applyOnce, 80));
})();
